import { useEffect, useState } from "react";
import { ReactComponent as InforCircle } from "assets/images/svgs/info-circle.svg";
import bgAttendess from "assets/images/others/host-side-images/bgAttendess.png";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "utils/helpers/enums/routes";
import AttendeeBreadcrumb, {
  AttendeBreadcrumbLink,
} from "components/breadcrumbs/AttendeeBreadcrumb";
import useAttendee from "../useHooks";
import { MinorDTO } from "utils/helpers/models/attendee/minor.dto";
import { AddMinorToEventDTO } from "utils/helpers/models/attendee/add-minor-to-event.dto";
import useUtils from "hooks/useUtils";
import useHost from "containers/host/useHooks";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import { useSelector } from "react-redux";
import { JoiningFilterDTO } from "utils/helpers/models/attendee/joining-filter-dto";
import WorkshopSessionPopup from "components/popups/workshop-session";
import ExperienceDetailHeader from "components/experience-detail-header";
import noFoundGif from "assets/images/gifs/noFoundGif.gif";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import useStore from "hooks/useStore";

const AttendeeJoining = () => {
  const breadcrumbLinks: AttendeBreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.attendeeHome },
    { title: "Joining", path: siteRoutes.attendeeJoining },
  ];
  const [formData, setFormData] = useState<AddMinorToEventDTO>(
    new AddMinorToEventDTO()
  );
  const [workshopSessionModal, setWorkshopSessionModal] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [minors, setMinors] = useState<MinorDTO[]>([]);
  const [event, setEvent] = useState<EventDTO>(new EventDTO());
  const [filters, setFilters] = useState<Partial<JoiningFilterDTO>>({
    page: 1,
    totalPages: 1,
    size: 10,
  });
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const { getQueryParams } = useUtils();
  const params = getQueryParams();
  const { getAttendeeMinors } = useAttendee();
  const { getEventById } = useHost();
  const navigate = useNavigate();
  const { isProfileCompleted } = useStore();

  // Check profile completion on mount - redirect to home if not completed
  useEffect(() => {
    const profileCompleted = isProfileCompleted();
    if (!profileCompleted) {
      navigate(siteRoutes.attendeeHome, { replace: true });
    }
  }, [isProfileCompleted, navigate]);

  useEffect(() => {
    getEventById(params?.id, setEvent);
  }, []);

  useEffect(() => {
    if (event?.ageGroup) {
      getAllMinors({ ...filters, ageGroup: event.ageGroup });
    }
  }, [event?.ageGroup]);

  // hide buuton on the base of startTime
  const lastSession = event.workshopSessions?.[event.workshopSessions.length - 1];
  const lastSessionDateTime = lastSession
    ? new Date(`${lastSession.startDateWithMonthAndYear} ${lastSession.endTime}`)
    : null;
  const hasStarted = lastSessionDateTime ? new Date() >= lastSessionDateTime : false;

  const visibleSessions = event.workshopSessions?.slice(0, 3) || [];
  const hasMoreSessions = (event.workshopSessions?.length || 0) > 3;

  const goToHostDetail = () => {
    navigate(`${siteRoutes.attendeeHostProfileDetail}?id=${event.host.id}`);
  };

  const handleSelectMinor = (id: number) => {
    // Prevent selection if event is in cancellation pending status
    if (isCancellationPending) {
      return;
    }

    const availableMinorsSlot = (event.joineeSlots || 0) - (event.eventSubscriptions?.length || 0);
    
    if (formData.minorId.includes(id)) {
      // Deselect: allow removing selected minor
      const index = formData.minorId.indexOf(id);
      formData.minorId.splice(index, 1);
      setFormData({ ...formData });
    } else {
      // Select: only allow if we haven't reached the slot limit
      if (formData.minorId.length < availableMinorsSlot && availableMinorsSlot > 0) {
        formData.minorId.push(id);
        setFormData({ ...formData });
      }
    }
  };

  const getAllMinors = (filters: Partial<JoiningFilterDTO>) => {
    const isFirstPage = filters.page === 1;
    getAttendeeMinors(
      setMinors,
      { ...filters, page: filters.page - 1, ageGroup: event?.ageGroup },
      isFirstPage ? [] : minors,
      setFilters
    );
    setFilters({ ...filters });
    
    // Mark initial load as complete after first API call
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  };

  const onSubmit = () => {
    // Prevent submission if event is in cancellation pending status
    if (isCancellationPending) {
      return;
    }

    const selectedMinors = minors.filter(
      (minor) =>
        formData.minorId.includes(minor.id) && !hasAlreadyJoined(minor.id)
    );

    navigate(siteRoutes.attendeeJoinAndPay, {
      state: {
        selectedMinors,
        eventId: params.id,
        acceptPolicy: formData.acceptPolicy,
      },
    });
  };

  const hasAlreadyJoined = (minorId: number): boolean => {
    return event.eventSubscriptions?.map((s) => s.id).includes(minorId) || false;
  };

  const availableMinorsSlot = (event.joineeSlots || 0) - (event.eventSubscriptions?.length || 0);
  const canSelectMore = formData.minorId.length < availableMinorsSlot;
  const selectedCount = formData.minorId.length;
  const isCancellationPending = String(event.eventStatus) === "CANCELLATION_PENDING";

  return (
    <div>
      <ExperienceDetailHeader
        event={event}
        breadcrumb={<AttendeeBreadcrumb links={breadcrumbLinks} />}
        onHostClick={goToHostDetail}
        onSeeMoreSessions={() => setWorkshopSessionModal(true)}
        visibleSessions={visibleSessions}
        hasMoreSessions={hasMoreSessions}
        gradientClass="host-bg-gradient"
      />
      <div className="container my-5">
        <div className="relative">
          <h3 className="text-blackie text-2xl font-medium mb-2 flex items-center gap-1">
            Select Participants
            <span
              className="cursor-pointer text-gray-500 hover:text-gray-700 relative flex"
              onMouseEnter={() => setIsTooltipVisible(true)}
              onMouseLeave={() => setIsTooltipVisible(false)}
            >
              <InforCircle />
            </span>
          </h3>
          {/* Tooltip */}
          {isTooltipVisible && (
            <div className="absolute w-[250px] bottom-18 left-20 p-4 rounded-lg  bg-primaryBlueBg text-white text-xs opacity-100 transition-opacity duration-300 z-10">
              <p className="text-xs">
                We understand unexpected cancellations may occur. However, out
                of respect for the host and other attendees, frequent
                cancellations, particularly from overbooking, may result in the
                family member profile being temporarily disabled.
              </p>
            </div>
          )}
          <p className="text-lightBlackText text-lg">
            Select Persons from your Family Members to join the Event.
          </p>
          {isCancellationPending && (
            <p className="text-sm text-red-600 mt-2 font-medium">
              This event is pending cancellation. New join requests are not currently being accepted.
            </p>
          )}
          {/* {availableMinorsSlot > 0 ? (
            <p className="text-sm text-gray-600 mt-1">
              Available slots: {availableMinorsSlot - selectedCount} of {availableMinorsSlot}
              {selectedCount > 0 && ` (${selectedCount} selected)`}
            </p>
          ) : (
            <p className="text-sm text-red-600 mt-1 font-medium">
              No slots available. This event is fully booked.
            </p>
          )} */}
        </div>
        <div className="flex items-center flex-wrap gap-6 py-5">
          {minors.map((minor, index) => (
            <div
              key={index}
              className={`relative flex flex-col items-center justify-center p-6 rounded-[18px] transition-all duration-300 border w-[235px] h-[260px] ${
                formData.minorId.includes(minor.id)
                  ? "border-yellowText "
                  : hasAlreadyJoined(minor.id) || isCancellationPending
                  ? "border-gray-300 opacity-60"
                  : !canSelectMore && !formData.minorId.includes(minor.id)
                  ? "border-gray-200 opacity-50 cursor-not-allowed"
                  : "border-gray-200 cursor-pointer"
              }`}
              style={{ backgroundImage: `url(${bgAttendess})` }}
              onClick={() =>
                !hasAlreadyJoined(minor.id) && !isCancellationPending && canSelectMore && handleSelectMinor(minor.id)
              }
            >
              {/* Clickable Checkbox */}
              <div
                className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center"
                onClick={(e) => e.stopPropagation()}
              >
                {hasAlreadyJoined(minor.id) ? (
                  <input
                    type="checkbox"
                    className="custom-checkbox"
                    disabled={true}
                    checked={true}
                  />
                ) : (
                  <input
                    type="checkbox"
                    className="custom-checkbox"
                    checked={formData.minorId.includes(minor.id)}
                    disabled={isCancellationPending || (!canSelectMore && !formData.minorId.includes(minor.id))}
                    onChange={() => handleSelectMinor(minor.id)}
                  />
                )}
              </div>

              {/* Attendee Image */}
              <img
                className="w-[110px] h-[110px] rounded-full "
                src={minor.profilePicUrl}
              />

              {/* Attendee Name */}
              <span className="text-lg font-semibold mt-3 text-blackie">
                {`${minor.firstName} ${minor.lastName}`}
              </span>
            </div>
          ))}
        </div>
        <div className="">
        <CustomMessageDisplay
          show={!isLoading && !minors.length && !isInitialLoad}
          title="No Family Members Available"
          slogan="You don't have any family members matching the event's age group. Add family members to join this event."
          image={noFoundGif}
          imageAlt="No family members found"
          className="py-12"
          imageClassName="w-24 h-24"
          titleClassName="text-2xl font-bold text-blackie"
          sloganClassName="text-gray-500 text-base"
        />
      </div>

        <div className="mt-4 flex items-center">
          <input
            type="checkbox"
            id="confirmation"
            className="mr-2 custom-checkbox"
            checked={formData.acceptPolicy}
            disabled={isCancellationPending}
            onChange={(e) =>
              setFormData({ ...formData, acceptPolicy: e.target.checked })
            }
          />

         
          <label htmlFor="confirmation" className="text-sm text-blackie">
            I certify that I have reviewed the event details and confirm the
            selected attendees meet the criteria and are not scheduled for
            another event at the same time.
          </label>
        </div>
        <button
          onClick={onSubmit}
          className="bg-primaryBlueText px-4 py-2 text-white rounded-full mt-6 disabled:bg-gray-300 disabled:cursor-not-allowed"
          disabled={isCancellationPending || !formData.minorId.length || !formData.acceptPolicy || availableMinorsSlot <= 0}
        >
          Confirm
        </button>
      </div>
     
      {workshopSessionModal && (
        <WorkshopSessionPopup
          isOpen={workshopSessionModal}
          onClose={() => setWorkshopSessionModal(false)}
          sessions={event.workshopSessions}
        />
      )}
    </div>
  );
};

export default AttendeeJoining;
